import * as Primitives from '@radix-ui/react-alert-dialog'
import * as React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { createRoot } from 'react-dom/client'

import { Button } from '@/components/base/button'
import { Heading } from '@/components/base/heading'
import { Input } from '@/components/base/input'
import { Label } from '@/components/base/label'
import { useAppForm } from '@/components/form/form'
import { detectBrowserLocale, localeSubject } from '@/i18n/i18n-provider'
import { I18nProvider, useI18n } from '@/i18n/i18n-react'
import { clx } from '@/utils/clx'

/** Imperative prompts mount into a detached React root — re-provide i18n there or `L` is undefined. */
const mountPromptRoot = (node: React.ReactNode) => {
	const container = document.createElement('div')
	document.body.appendChild(container)
	const root = createRoot(container)
	root.render(
		<I18nProvider locale$={localeSubject} defaultLocales={[detectBrowserLocale()]}>
			{node}
		</I18nProvider>
	)
	return {
		unmount: () => {
			root.unmount()
			container.remove()
		}
	}
}

type PromptVariant = 'danger' | 'confirmation'

const PromptContext = React.createContext<{ variant: PromptVariant }>({
	variant: 'danger'
})

const usePromptContext = () => {
	const context = React.useContext(PromptContext)
	if (!context) {
		throw new Error('usePromptContext must be used within a PromptProvider')
	}
	return context
}

type PromptProviderProps = React.PropsWithChildren<{
	variant: PromptVariant
}>

const PromptProvider = ({ variant, children }: PromptProviderProps) => {
	return <PromptContext.Provider value={{ variant }}>{children}</PromptContext.Provider>
}

/**
 * This component is based on the [Radix UI Alert Dialog](https://www.radix-ui.com/primitives/docs/components/alert-dialog) primitives.
 */
const Root = ({
	/**
	 * The variant of the prompt.
	 */
	variant = 'danger',
	...props
}: React.ComponentPropsWithoutRef<typeof Primitives.Root> & {
	variant?: PromptVariant
}) => {
	return (
		<PromptProvider variant={variant}>
			<Primitives.Root {...props} />
		</PromptProvider>
	)
}
Root.displayName = 'Prompt'

const Trigger = Primitives.Trigger
Trigger.displayName = 'Prompt.Trigger'

const Portal = (props: Primitives.AlertDialogPortalProps) => {
	return <Primitives.AlertDialogPortal {...props} />
}
Portal.displayName = 'Prompt.Portal'

const Overlay = React.forwardRef<
	React.ElementRef<typeof Primitives.Overlay>,
	React.ComponentPropsWithoutRef<typeof Primitives.Overlay>
>(({ className, ...props }, ref) => {
	return (
		<Primitives.Overlay
			ref={ref}
			className={clx(
				'fixed inset-0 z-40 bg-ui-bg-overlay',
				'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
				className
			)}
			{...props}
		/>
	)
})
Overlay.displayName = 'Prompt.Overlay'

const Title = React.forwardRef<
	React.ElementRef<typeof Primitives.Title>,
	Omit<React.ComponentPropsWithoutRef<typeof Primitives.Title>, 'asChild'>
>(({ className, children, ...props }, ref) => {
	return (
		<Primitives.Title ref={ref} className={clx(className)} {...props} asChild>
			<Heading level='h2' className='text-ui-fg-base'>
				{children}
			</Heading>
		</Primitives.Title>
	)
})
Title.displayName = 'Prompt.Title'

const Content = React.forwardRef<
	React.ElementRef<typeof Primitives.Content>,
	React.ComponentPropsWithoutRef<typeof Primitives.Content>
>(({ className, ...props }, ref) => {
	return (
		<Portal>
			<Overlay />
			<Primitives.Content
				ref={ref}
				className={clx(
					'fixed left-[50%] top-[50%] z-50 flex w-full max-w-[400px] translate-x-[-50%] translate-y-[-50%] flex-col rounded-lg border bg-ui-bg-base shadow-elevation-flyout focus-visible:outline-none',
					'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] duration-200',
					className
				)}
				{...props}
			/>
		</Portal>
	)
})
Content.displayName = 'Prompt.Content'

const Description = React.forwardRef<
	React.ElementRef<typeof Primitives.Description>,
	React.ComponentPropsWithoutRef<typeof Primitives.Description>
>(({ className, ...props }, ref) => {
	return (
		<Primitives.Description
			ref={ref}
			className={clx('txt-compact-medium text-ui-fg-subtle', className)}
			{...props}
		/>
	)
})
Description.displayName = 'Prompt.Description'

const Action = React.forwardRef<
	React.ElementRef<typeof Primitives.Action>,
	Omit<React.ComponentPropsWithoutRef<typeof Primitives.Action>, 'asChild'>
>(({ className, children, type, ...props }, ref) => {
	const { variant } = usePromptContext()

	return (
		<Primitives.Action ref={ref} className={className} {...props} asChild>
			<Button size='small' type={type} variant={variant === 'danger' ? 'danger' : 'primary'}>
				{children}
			</Button>
		</Primitives.Action>
	)
})
Action.displayName = 'Prompt.Action'

const Cancel = React.forwardRef<
	React.ElementRef<typeof Primitives.Cancel>,
	Omit<React.ComponentPropsWithoutRef<typeof Primitives.Cancel>, 'asChild'>
>(({ className, children, ...props }, ref) => {
	return (
		<Primitives.Cancel ref={ref} className={clx(className)} {...props} asChild>
			<Button size='small' variant='secondary'>
				{children}
			</Button>
		</Primitives.Cancel>
	)
})
Cancel.displayName = 'Prompt.Cancel'

/**
 * This component is based on the `div` element and supports all of its props
 */
const Header = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
	return <div className={clx('flex flex-col gap-y-1 px-6 pt-6', className)} {...props} />
}
Header.displayName = 'Prompt.Header'

/**
 * This component is based on the `div` element and supports all of its props
 */
const Footer = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
	return <div className={clx('flex items-center justify-end gap-x-2 p-6', className)} {...props} />
}
Footer.displayName = 'Prompt.Footer'

const Prompt = Object.assign(Root, {
	Trigger,
	Content,
	Title,
	Description,
	Action,
	Cancel,
	Header,
	Footer
})

export { Prompt }

export interface ConfirmPromptProps {
	open: boolean
	title: string
	description: string
	variant?: 'danger' | 'confirmation'
	verificationText?: string
	verificationInstruction?: string
	cancelText?: string
	confirmText?: string
	onConfirm: () => void
	onCancel: () => void
}

export const ConfirmPromptComponent = ({
	open,
	variant = 'danger',
	title,
	description,
	verificationText,
	verificationInstruction,
	cancelText,
	confirmText,
	onConfirm,
	onCancel
}: ConfirmPromptProps) => {
	const { L } = useI18n()
	const [userInput, setUserInput] = useState('')

	const handleUserInput = (event: React.ChangeEvent<HTMLInputElement>) => {
		setUserInput(event.target.value)
	}

	const validInput = useMemo(() => {
		if (!verificationText) {
			return true
		}

		return userInput === verificationText
	}, [userInput, verificationText])

	const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault()

		if (!verificationText) {
			return
		}

		if (validInput) {
			onConfirm()
		}
	}

	useEffect(() => {
		const handleEscape = (event: KeyboardEvent) => {
			if (event.key === 'Escape' && open) {
				onCancel()
			}
		}

		document.addEventListener('keydown', handleEscape)

		return () => {
			document.removeEventListener('keydown', handleEscape)
		}
	}, [onCancel, open])

	return (
		<Prompt open={open} variant={variant}>
			<Prompt.Content>
				<form onSubmit={handleFormSubmit}>
					<Prompt.Header>
						<Prompt.Title>{title}</Prompt.Title>
						<Prompt.Description>{description}</Prompt.Description>
					</Prompt.Header>
					{verificationText && (
						<div className='mt-6 flex flex-col gap-y-4 border-y border-ui-border-base p-6'>
							<Label htmlFor='verificationText' className='text-ui-fg-subtle'>
								{(() => {
									// The translation carries a `{0}` slot for the name. Split around it so the name
									// can be rendered with emphasis styling instead of embedding raw HTML in the string.
									const [before = '', after = ''] =
										L.General.VerificationInstruction('\u0000').split('\u0000')
									return (
										<>
											{before}
											<span className='txt-compact-medium-plus text-ui-fg-base'>
												{verificationText}
											</span>
											{after}
										</>
									)
								})()}
							</Label>
							<Input
								autoFocus
								autoComplete='off'
								id='verificationText'
								placeholder={verificationText}
								onChange={handleUserInput}
							/>
						</div>
					)}
					<Prompt.Footer>
						<Prompt.Cancel onClick={onCancel}>{cancelText || L.General.Cancel()}</Prompt.Cancel>
						<Prompt.Action
							disabled={!validInput}
							type={verificationText ? 'submit' : 'button'}
							onClick={verificationText ? undefined : onConfirm}
						>
							{confirmText || L.General.Confirm()}
						</Prompt.Action>
					</Prompt.Footer>
				</form>
			</Prompt.Content>
		</Prompt>
	)
}

export const usePrompt = () => {
	const prompt = async (props: Omit<ConfirmPromptProps, 'onConfirm' | 'onCancel' | 'open'>): Promise<boolean> => {
		return new Promise(resolve => {
			let open = true
			let mounted: { unmount: () => void } | undefined

			const onCancel = () => {
				open = false
				mounted?.unmount()
				resolve(false)

				// TEMP FIX for Radix issue with dropdowns persisting pointer-events: none on body after closing
				document.body.style.pointerEvents = 'auto'
			}

			const onConfirm = () => {
				open = false
				resolve(true)
				mounted?.unmount()

				// TEMP FIX for Radix issue with dropdowns persisting pointer-events: none on body after closing
				document.body.style.pointerEvents = 'auto'
			}

			mounted = mountPromptRoot(
				<ConfirmPromptComponent open={open} onConfirm={onConfirm} onCancel={onCancel} {...props} />
			)
		})
	}

	return prompt
}

export interface TextPromptProps {
	open: boolean
	title: string
	description?: string
	instruction?: string
	placeholder?: string
	cancelText?: string
	confirmText?: string
	isValid?: (input: string) => string | undefined
	onConfirm: (input: string) => void
	onCancel: () => void
}

export const TextPromptComponent = ({
	open,
	title,
	description,
	instruction,
	placeholder,
	cancelText = 'Cancel',
	confirmText = 'Confirm',
	isValid = () => undefined,
	onConfirm,
	onCancel
}: TextPromptProps) => {
	const form = useAppForm({
		defaultValues: {
			userInput: ''
		},
		onSubmit: async ({ value }) => {
			onConfirm(value.userInput)
		}
	})

	useEffect(() => {
		const handleEscape = (event: KeyboardEvent) => {
			if (event.key === 'Escape' && open) {
				onCancel()
			}
		}

		document.addEventListener('keydown', handleEscape)

		return () => {
			document.removeEventListener('keydown', handleEscape)
		}
	}, [onCancel, open])

	return (
		<Prompt open={open} variant='confirmation'>
			<Prompt.Content>
				<form.AppForm>
					<form
						onSubmit={event => {
							event.preventDefault()
							void form.handleSubmit()
						}}
					>
						<Prompt.Header>
							<Prompt.Title>{title}</Prompt.Title>
							<Prompt.Description>{description}</Prompt.Description>
						</Prompt.Header>
						<div className='mt-6 flex flex-col gap-y-4 border-y border-ui-border-base p-6'>
							{!!instruction && (
								<Label htmlFor='textInput' className='text-ui-fg-subtle'>
									{instruction}
								</Label>
							)}
							<form.AppField
								name='userInput'
								validators={{
									onChange: ({ value }) => isValid(value)
								}}
								children={field => (
									<field.TextField autoFocus autoComplete='off' placeholder={placeholder} />
								)}
							/>
						</div>
						<Prompt.Footer>
							<Prompt.Cancel onClick={onCancel}>{cancelText}</Prompt.Cancel>
							<form.Subscribe
								selector={state => ({ isSubmitting: state.isSubmitting, isValid: state.isValid })}
							>
								{({ isValid }) => {
									return (
										<Prompt.Action disabled={!isValid} type='submit'>
											{confirmText}
										</Prompt.Action>
									)
								}}
							</form.Subscribe>
						</Prompt.Footer>
					</form>
				</form.AppForm>
			</Prompt.Content>
		</Prompt>
	)
}

export const useTextPrompt = () => {
	return useCallback(
		async (props: Omit<TextPromptProps, 'onConfirm' | 'onCancel' | 'open'>): Promise<string | undefined> => {
			return new Promise<string | undefined>(resolve => {
				let open = true
				let mounted: { unmount: () => void } | undefined

				const onCancel = () => {
					open = false
					mounted?.unmount()
					resolve(undefined)

					// TEMP FIX for Radix issue with dropdowns persisting pointer-events: none on body after closing
					document.body.style.pointerEvents = 'auto'
				}

				const onConfirm = (input: string) => {
					open = false
					resolve(input)
					mounted?.unmount()

					// TEMP FIX for Radix issue with dropdowns persisting pointer-events: none on body after closing
					document.body.style.pointerEvents = 'auto'
				}

				mounted = mountPromptRoot(
					<TextPromptComponent open={open} onConfirm={onConfirm} onCancel={onCancel} {...props} />
				)
			})
		},
		[]
	)
}
